import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthService } from "../../services/AuthService";
import hubLogo from "../../assets/branding/logoHUB.png";

interface LoginForm {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginForm>({
    username: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const EyeOpen = (
    <svg viewBox="0 0 24 24" className="w-4 h-4 block" aria-hidden="true">
      <path d="M2.5 12s3.5-5.5 9.5-5.5S21.5 12 21.5 12s-3.5 5.5-9.5 5.5S2.5 12 2.5 12Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );

  const EyeClosed = (
    <svg viewBox="0 0 24 24" className="w-4 h-4 block" aria-hidden="true">
      <path d="M3 3l18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.5 6.8C3.8 8.7 2 12 2 12s3.5 5.5 9.5 5.5c2 0 3.7-.4 5.2-1.1M13.9 13.9a3 3 0 01-3.8-3.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.1 5.4A10 10 0 0112 4.5C18 4.5 21.5 10 21.5 10s-.8 1.2-2.1 2.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await AuthService.login(formData);
      if (response.access_token) {
        const from = (location.state as any)?.from?.pathname || "/";
        navigate(from, { replace: true });
      } else {
        throw new Error("Token não recebido");
      }
    } catch (err) {
      setError("Usuário ou senha inválidos. Por favor, tente novamente.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex justify-center items-center relative overflow-hidden p-[24px] sm:p-[18px]"
      style={{
        background: `
          radial-gradient(circle at 50% 42%, rgba(212, 226, 244, 0.98) 0%, rgba(188, 208, 236, 0.9) 50%, rgba(164, 188, 226, 0.82) 78%, rgba(146, 172, 214, 0.72) 100%),
          linear-gradient(135deg, rgba(134, 164, 210, 0.34) 0%, rgba(112, 144, 200, 0.28) 50%, rgba(96, 128, 186, 0.26) 100%)
        `,
        isolation: "isolate",
        color: "#e9f0ff",
      }}
    >
      {/* Background pseudo-element logic */}
      <div
        className="absolute inset-[-12%] z-0 pointer-events-none blur-[16px]"
        style={{
          background: `
            radial-gradient(circle at 18% 18%, rgba(90, 130, 190, 0.08), transparent 30%),
            radial-gradient(circle at 82% 16%, rgba(100, 150, 210, 0.08), transparent 32%),
            radial-gradient(circle at 56% 76%, rgba(85, 125, 185, 0.07), transparent 38%)
          `
        }}
      />

      <div
        className="relative z-[1] w-[min(370px,90vw)] flex flex-col gap-[16px] p-[26px_22px] rounded-[16px] sm:p-[22px_20px]"
        style={{
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 248, 255, 0.96))",
          border: "1px solid rgba(10, 28, 70, 0.06)",
          boxShadow: "0 18px 36px rgba(14, 52, 161, 0.14), 0 0 0 1px rgba(10, 28, 70, 0.05)",
          backdropFilter: "blur(2px)",
        }}
      >
        <div className="flex flex-col items-center gap-[10px] pb-[12px] border-b border-[rgba(10,28,70,0.06)] sm:items-center">
          <div className="flex flex-col gap-[4px] items-center text-center">
            <span className="uppercase tracking-[0.07em] text-[12px] text-[#0b1f44] font-extrabold mt-[2px]"></span>
            <h1 className="text-[#0b1f44] text-[32px] m-0 mb-[12px] font-extrabold sm:text-[26px] leading-[1.2]">
              Dashboard
              <br />
              de Promoção
            </h1>
            <p className="text-[#33405d] text-[14px] m-0 max-w-[320px] leading-[1.4]">
              Entre para acompanhar os painéis, relatórios e notificações.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-[14px] pb-[6px]" autoComplete="off">
          {error && (
            <div
              className="px-[12px] py-[10px] rounded-[9px] text-[13px] text-center"
              style={{
                background: "rgba(217, 70, 70, 0.12)",
                color: "#ffb5bd",
                border: "1px solid rgba(217, 70, 70, 0.4)",
              }}
            >
              {error}
            </div>
          )}

          <div className="flex flex-col gap-[6px] w-[min(320px,100%)] self-center">
            <label htmlFor="username" className="text-[#0b1f44] text-[13px] font-bold tracking-[0.01em]">
              E-mail ou Usuário
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="E-mail ou usuário"
              autoComplete="username"
              className="w-full px-[12px] py-[10px] rounded-[10px] border border-[rgba(10,28,70,0.15)] bg-[rgba(255,255,255,0.8)] text-[#0b1f44] text-[14px] transition-all duration-[0.18s] ease-in-out placeholder-[#6b7690] focus:outline-none focus:border-[rgba(31,91,255,0.6)] focus:shadow-[0_0_0_1px_rgba(31,91,255,0.25)] focus:bg-[rgba(255,255,255,0.9)]"
            />
          </div>

          <div className="flex flex-col gap-[6px] w-[min(320px,100%)] self-center">
            <label htmlFor="password" className="text-[#0b1f44] text-[13px] font-bold tracking-[0.01em]">
              Senha
            </label>
            <div className="relative flex items-center w-[min(320px,100%)]">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Sua senha"
                autoComplete="off"
                className="w-full pr-[42px] px-[12px] py-[10px] rounded-[10px] border border-[rgba(10,28,70,0.15)] bg-[rgba(255,255,255,0.8)] text-[#0b1f44] text-[14px] transition-all duration-[0.18s] ease-in-out placeholder-[#6b7690] focus:outline-none focus:border-[rgba(31,91,255,0.6)] focus:shadow-[0_0_0_1px_rgba(31,91,255,0.25)] focus:bg-[rgba(255,255,255,0.9)]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-[12px] px-[10px] py-[6px] rounded-[10px] text-[#0b1f44] text-[14px] cursor-pointer transition-all duration-[0.16s] ease-in-out hover:bg-[rgba(10,28,70,0.12)] focus:outline-none focus:shadow-[0_0_0_1px_rgba(31,91,255,0.4)]"
                style={{
                  background: "rgba(10, 28, 70, 0.06)",
                  border: "1px solid rgba(10, 28, 70, 0.12)",
                  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.06)"
                }}
              >
                {showPassword ? EyeClosed : EyeOpen}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-[min(240px,100%)] self-center mt-[6px] px-[12px] py-[11px] rounded-[12px] text-[16px] font-bold tracking-[0.01em] cursor-pointer transition-[transform,box-shadow,filter] duration-[0.15s] ease-in-out disabled:opacity-70 disabled:cursor-not-allowed hover:not(:disabled):-translate-y-[1px] hover:not(:disabled):brightness-105 hover:not(:disabled):shadow-[0_14px_32px_rgba(11,61,145,0.38),0_0_0_1px_rgba(255,255,255,0.08)]"
            style={{
              background: "linear-gradient(120deg, #0b3d91, #1b63ff)",
              color: "#eaf3ff",
              border: "1px solid rgba(255, 255, 255, 0.22)",
              boxShadow: "0 12px 28px rgba(11, 61, 145, 0.32), 0 0 0 1px rgba(255, 255, 255, 0.06)",
            }}
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </div>

      <footer className="fixed left-1/2 bottom-[10px] -translate-x-1/2 inline-flex items-center gap-[8px] px-[10px] py-[6px] rounded-[10px] bg-transparent border-none text-[#0b1f44] font-bold z-[2]">
        <span className="text-[11px] tracking-[0.06em] uppercase opacity-80">Criado por</span>
        <div className="inline-flex items-center gap-[8px]">
          <img src={hubLogo} alt="HUB Brasil" className="h-[16px] w-auto block" />
        </div>
      </footer>
    </div>
  );
};

export default Login;
